import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';

import { getLocalDocumentUrlFromS3 } from '@/api/document';
import {
  adultConfirmKycRequest,
  getHolderDetailAndRequiredDocumentsRequest,
} from '@/api/operation';

import { DocumentThumbnailFromUri } from '@/components/DataDisplay/File/DocumentThumbnailFromUri';
import { InfoReviewFieldValue } from '@/components/Form/Form.styled';
import { Icon } from '@/components/Icon/Icon';
import { DrawerProps } from '@/components/Layout/Drawer';
import { Drawer } from '@/components/Layout/Drawer/Drawer';
import { InfoReviewFieldSubValue } from '@/components/Layout/Drawer/Drawer.styled';

import { MINOR_IDENTITY_DOCUMENTS, TitleText } from '@/constants/user';

import { useAppDispatch, useAppSelector } from '@/hooks/redux';
import { useTranslate } from '@/hooks/useTranslate';

import { selectLoggedInUser } from '@/redux/auth/selectors';
import { selectConfigsState } from '@/redux/configs';
import { selectOperationUser } from '@/redux/operation/selectors';
import { showFileViewer } from '@/redux/ui';
import { selectUserHolderJointMinor } from '@/redux/user-data/selectors';

import { DocumentSection, DocumentStatus, SourceType } from '@/types/document';
import { User } from '@/types/user';

import { getAPIErrorMessage } from '@/utils/api';
import { getDataTestId } from '@/utils/browser';
import { formatDateValue } from '@/utils/string';
import { getDisplayCountryName } from '@/utils/user';

import {
  IdentityCheckReviewActions,
  IdentityCheckReviewContent,
  IdentityCheckReviewScroll,
} from './Drawers.styled';

type MinorIdentityCheckReviewDrawerProps = {
  onConfirmSuccess?: () => void;
  /** Called when "Redo ID check" succeeds - navigates to Verify Child ID page */
  onRedoIdCheckSuccess?: () => void;
} & DrawerProps;

function getTitleDisplay(title: User['title']): string {
  if (title == null) return '';
  return TitleText[String(title)] ?? '';
}

function formatFullName(user: User): string {
  const titleStr = getTitleDisplay(user.title);
  const nameParts = [user.firstName, user.middleName, user.lastName]
    .filter(Boolean)
    .map(String);
  const full = titleStr
    ? [titleStr, ...nameParts].join(' ')
    : nameParts.join(' ');
  return full || '–';
}

function formatGenderMarital(user: User, t: (key: string) => string): string {
  const parts: string[] = [];
  if (user.gender) {
    const genderKey = user.gender === 'M' ? 'gender.male' : 'gender.female';
    parts.push(t(genderKey) || user.gender);
  }
  if (user.maritalStatus) {
    const maritalKey = `maritalStatus.${String(user.maritalStatus).toLowerCase()}`;
    parts.push(t(maritalKey) || user.maritalStatus);
  }
  return parts.join(', ');
}

export function MinorIdentityCheckReviewDrawer({
  open,
  onClose,
  title,
  onConfirmSuccess,
  onRedoIdCheckSuccess,
  ...props
}: MinorIdentityCheckReviewDrawerProps) {
  const { translateIfExists, t, language } = useTranslate();
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const { dateFormat } = useAppSelector(selectConfigsState);
  const loggedUser = useAppSelector(selectLoggedInUser);
  const holderJointMinor = useAppSelector(selectUserHolderJointMinor);
  const minorUser = useAppSelector(selectOperationUser) ?? undefined;

  const operationId = loggedUser?.operationId ?? '';
  const clientId = holderJointMinor?.clientId ?? 0;

  const [imageLoadError, setImageLoadError] = useState<string | null>(null);

  const openDocumentInViewer = useCallback(
    async (uri: string) => {
      setImageLoadError(null);
      try {
        const url = await getLocalDocumentUrlFromS3(uri);
        const type = SourceType.Image;
        dispatch(showFileViewer({ src: url, type }));
      } catch (err) {
        setImageLoadError(getAPIErrorMessage(err));
      }
    },
    [dispatch],
  );

  const { mutate: confirmKyc, isPending: isConfirmPending } = useMutation({
    mutationFn: (confirmed: boolean) =>
      adultConfirmKycRequest({ operationId, clientId, confirmed }),
    onSuccess: (_data, confirmed) => {
      if (confirmed) {
        onClose();
        onConfirmSuccess?.();
      } else {
        onRedoIdCheckSuccess?.();
      }
      queryClient.invalidateQueries({
        queryKey: [
          'minor-holder-details-and-required-documents',
          operationId,
          clientId,
        ],
      });
      if (!confirmed && operationId && clientId) {
        queryClient.prefetchQuery({
          queryKey: [
            'minor-holder-details-and-required-documents',
            operationId,
            clientId,
          ],
          queryFn: () =>
            getHolderDetailAndRequiredDocumentsRequest(operationId, clientId),
        });
      }
    },
  });

  const handleConfirm = () => confirmKyc(true);
  const handleRedoIdCheck = () => confirmKyc(false);

  if (!minorUser) return null;

  const documents =
    minorUser.documents?.filter(
      d => d.uri && d.status !== DocumentStatus.Rejected,
    ) ?? [];
  const identityDocs = documents.filter(d =>
    [DocumentSection.Identity, DocumentSection.IdentityBack].includes(d.type),
  );
  const identityDoc = documents.find(d => d.type === DocumentSection.Identity);
  const identityDocNumber = identityDoc?.docNumber ?? '–';
  const identityDocType = translateIfExists(
    `documentType.${identityDoc?.docType}`,
  );

  const hasIdentityDocs = MINOR_IDENTITY_DOCUMENTS.every(docType =>
    documents.some(d => d.type === docType),
  );

  return (
    <Drawer
      {...props}
      open={open}
      onClose={onClose}
      title={title}
      bgColor={(theme: Theme) => theme.palette.background.paper}
      headerBgColor={(theme: Theme) => theme.palette.background.paper}
    >
      <IdentityCheckReviewContent>
        <IdentityCheckReviewScroll>
          <Typography variant="body2">
            {t('moreInfo.form.identityCheckReview.subtitle')}
          </Typography>

          <Accordion defaultExpanded disableGutters elevation={0}>
            <AccordionSummary
              expandIcon={<Icon name="arrow-down" />}
              aria-controls="id-content"
              id="id-header"
            >
              <Typography variant="h5" component="span">
                {t('moreInfo.form.identityCheckReview.id.title')}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography
                variant="caption"
                sx={{
                  mb: 1.5,
                }}
              >
                {t('moreInfo.form.identityCheckReview.id.description')}
              </Typography>
              <Stack direction="column" spacing={1.5}>
                {identityDocs.map(doc =>
                  doc.uri ? (
                    <DocumentThumbnailFromUri
                      key={`${doc.type}-${doc.id}`}
                      uri={doc.uri}
                      contentType={doc.contentType}
                      onClick={openDocumentInViewer}
                      error={imageLoadError}
                    />
                  ) : null,
                )}
              </Stack>
            </AccordionDetails>
          </Accordion>

          <Accordion defaultExpanded disableGutters elevation={0}>
            <AccordionSummary
              expandIcon={<Icon name="arrow-down" />}
              aria-controls="personal-details-content"
              id="personal-details-header"
            >
              <Typography variant="h5" component="span">
                {t('moreInfo.form.identityCheckReview.personalDetails.title')}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="caption">
                {t(
                  'moreInfo.form.identityCheckReview.personalDetails.fullName',
                )}
              </Typography>
              <InfoReviewFieldValue sx={{ mt: 0.5 }}>
                {formatFullName(minorUser)}
              </InfoReviewFieldValue>
              <InfoReviewFieldSubValue sx={{ mt: 0.5 }}>
                {formatGenderMarital(minorUser, t)
                  ? `${formatGenderMarital(minorUser, t)}`
                  : ''}
              </InfoReviewFieldSubValue>
              <Typography
                variant="caption"
                sx={{
                  mt: 2,
                }}
              >
                {t(
                  'moreInfo.form.identityCheckReview.personalDetails.identityType',
                )}
              </Typography>
              <InfoReviewFieldValue sx={{ mt: 0.5 }}>
                {identityDoc?.docType
                  ? `${identityDocType} – ${identityDocNumber}`
                  : '–'}
              </InfoReviewFieldValue>

              <InfoReviewFieldSubValue sx={{ mt: 0.5 }}>
                {identityDoc?.expiryDate && (
                  <>
                    {t('moreInfo.form.identityCheckReview.expiresOn', {
                      date: formatDateValue(identityDoc.expiryDate, dateFormat),
                    })}
                  </>
                )}
              </InfoReviewFieldSubValue>
              <Typography
                variant="caption"
                sx={{
                  mt: 2,
                }}
              >
                {t(
                  'moreInfo.form.identityCheckReview.personalDetails.citizenship',
                )}
              </Typography>
              <InfoReviewFieldValue sx={{ mt: 0.5 }}>
                {getDisplayCountryName(
                  minorUser.citizenship || minorUser.nationality,
                  language,
                ) || '–'}
              </InfoReviewFieldValue>

              <Typography
                variant="caption"
                sx={{
                  mt: 2,
                  display: 'block',
                }}
              >
                {t(
                  'moreInfo.form.identityCheckReview.personalDetails.birthInfo',
                )}
              </Typography>
              <InfoReviewFieldValue>
                {[
                  getDisplayCountryName(minorUser.countryOfBirth, language),
                  minorUser.birthday
                    ? formatDateValue(minorUser.birthday, dateFormat)
                    : null,
                ]
                  .filter(Boolean)
                  .join(' – ') || '–'}
              </InfoReviewFieldValue>
            </AccordionDetails>
          </Accordion>

          {imageLoadError && (
            <Typography
              variant="caption"
              color="error"
              sx={{ mt: 1, display: 'block' }}
            >
              {imageLoadError}
            </Typography>
          )}
        </IdentityCheckReviewScroll>

        <IdentityCheckReviewActions>
          <Stack direction="column" spacing={1.5} width="100%">
            <Button
              variant="outlined"
              fullWidth
              onClick={handleRedoIdCheck}
              disabled={isConfirmPending}
              {...getDataTestId('identity-check-review-redo-button')}
            >
              {t('moreInfo.form.identityCheckReview.redoButton')}
            </Button>
            <Button
              variant="contained"
              fullWidth
              onClick={handleConfirm}
              disabled={isConfirmPending || !hasIdentityDocs}
              {...getDataTestId('identity-check-review-confirm-button')}
            >
              {isConfirmPending ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                t('common.confirm')
              )}
            </Button>
          </Stack>
        </IdentityCheckReviewActions>
      </IdentityCheckReviewContent>
    </Drawer>
  );
}
